import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchLibraryPage, searchLibrary } from '../services/library.service';
import { fetchProductPrices } from '../services/stripe.service';
import { PRODUCT_ID_LIBRARY } from '../config/constants';

const PAGE_SIZE = 12;
const MAX_PAGES_TO_COUNT = 200;

function normalize(s) {
	return (s ?? '').toString().trim().toLowerCase();
}

function tokenize(s) {
	return normalize(s).split(/\s+/).filter(Boolean);
}

function useDebouncedValue(value, delay = 300) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(id);
	}, [value, delay]);
	return debounced;
}

// Encapsulates all pattern library state: pagination, search, and price enrichment.
// Used by PatternLibrary component.
export const useLibrary = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const initialQ = searchParams.get('q') ?? '';
	const [searchTerm, setSearchTerm] = useState(initialQ);

	const [patterns, setPatterns] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchError, setSearchError] = useState(null);
	const [page, setPage] = useState(0);
	const cursorsRef = useRef([]);

	const [totalCount, setTotalCount] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	const [searchResults, setSearchResults] = useState([]);
	const [searchPage, setSearchPage] = useState(0);

	const [prices, setPrices] = useState([]);

	const normalized = useMemo(() => normalize(searchTerm), [searchTerm]);
	const debounced = useDebouncedValue(normalized, 300);
	const tokens = useMemo(() => tokenize(debounced), [debounced]);

	const priceMap = useMemo(() => {
		const m = new Map();
		for (const p of prices) m.set(p.id, p);
		return m;
	}, [prices]);

	const enrich = useCallback(
		(docs) =>
			docs.map((d) => ({
				...d,
				amount: d.priceId
					? (priceMap.get(d.priceId)?.data?.unit_amount ?? null)
					: null,
			})),
		[priceMap],
	);

	// Load library prices once
	useEffect(() => {
		fetchProductPrices(PRODUCT_ID_LIBRARY).then(setPrices);
	}, []);

	const ensureCursorsUpTo = useCallback(async (targetIndex) => {
		let lastKnown = -1;
		for (let i = cursorsRef.current.length - 1; i >= 0; i--) {
			if (cursorsRef.current[i]) {
				lastKnown = i;
				break;
			}
		}

		if (lastKnown < 0) {
			const { docs, lastDoc } = await fetchLibraryPage(null);
			if (lastDoc) cursorsRef.current[0] = lastDoc;
			lastKnown = 0;
			setTotalPages(1);
			setTotalCount(docs.length);
			if (docs.length < PAGE_SIZE) return;
		}

		let currentIndex = lastKnown;
		while (
			currentIndex < targetIndex &&
			currentIndex < MAX_PAGES_TO_COUNT - 1
		) {
			const { docs, lastDoc } = await fetchLibraryPage(
				cursorsRef.current[currentIndex],
			);
			if (!lastDoc) break;
			cursorsRef.current[currentIndex + 1] = lastDoc;
			currentIndex += 1;

			const tentativeCount = currentIndex * PAGE_SIZE + docs.length;
			setTotalCount(tentativeCount);
			setTotalPages(currentIndex + (docs.length > 0 ? 1 : 0));

			if (docs.length < PAGE_SIZE) break;
		}
	}, []);

	const computeAllPages = useCallback(async () => {
		if (totalPages > 1 && totalCount >= PAGE_SIZE) return;

		const knownPages = cursorsRef.current.filter(Boolean).length;
		let lastCursor =
			cursorsRef.current[Math.max(0, knownPages - 1)] ?? null;
		let fetchedPages = knownPages;
		let runningCount = knownPages * PAGE_SIZE;

		while (fetchedPages < MAX_PAGES_TO_COUNT) {
			const { docs, lastDoc } = await fetchLibraryPage(lastCursor);
			if (!lastDoc) break;

			lastCursor = lastDoc;
			cursorsRef.current[fetchedPages] = lastCursor;
			runningCount += docs.length;
			fetchedPages += 1;

			setTotalCount(runningCount);
			setTotalPages(fetchedPages);

			if (docs.length < PAGE_SIZE) break;
		}
	}, [totalPages, totalCount]);

	const loadPage = useCallback(
		async (targetPage) => {
			setLoading(true);
			try {
				let cursor = null;
				if (targetPage > 0) {
					if (!cursorsRef.current[targetPage - 1]) {
						await ensureCursorsUpTo(targetPage - 1);
					}
					cursor = cursorsRef.current[targetPage - 1] ?? null;
				}

				const { docs, lastDoc } = await fetchLibraryPage(cursor);
				if (lastDoc) cursorsRef.current[targetPage] = lastDoc;

				setPatterns(enrich(docs));
				setPage(targetPage);
			} finally {
				setLoading(false);
			}
		},
		[enrich, ensureCursorsUpTo],
	);

	// Non-search boot
	useEffect(() => {
		if (debounced.length === 0) {
			cursorsRef.current = [];
			setTotalPages(1);
			setTotalCount(0);
			loadPage(0).then(() => computeAllPages());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debounced, priceMap]);

	// Search mode
	useEffect(() => {
		let alive = true;

		if (debounced.length === 0) {
			setSearchResults([]);
			setSearchPage(0);
			return;
		}

		setLoading(true);
		setSearchError(null);
		searchLibrary(debounced, tokens)
			.then((results) => {
				if (!alive) return;
				const enriched = enrich(results);
				setSearchResults(enriched);
				setSearchPage(0);
				setTotalCount(enriched.length);
				setTotalPages(
					Math.max(1, Math.ceil(enriched.length / PAGE_SIZE)),
				);
			})
			.catch((err) => {
				if (alive)
					setSearchError(
						err.message || 'Search failed. Please try again.',
					);
			})
			.finally(() => {
				if (alive) setLoading(false);
			});

		return () => {
			alive = false;
		};
	}, [debounced, tokens, enrich]);

	// URL ↔ state sync
	useEffect(() => {
		const q = searchParams.get('q') ?? '';
		if (q !== searchTerm) setSearchTerm(q);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	useEffect(() => {
		const next = new URLSearchParams(searchParams);
		const currentQ = next.get('q') ?? '';
		const nextQ = debounced ?? '';
		if (currentQ !== nextQ) {
			if (nextQ) next.set('q', nextQ);
			else next.delete('q');
			setSearchParams(next, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debounced]);

	// Reset pagination when mode changes
	useEffect(() => {
		if (debounced.length > 0) {
			setPage(0);
			cursorsRef.current = [];
		} else {
			setSearchPage(0);
			setSearchResults([]);
		}
	}, [debounced]);

	const isSearchMode = debounced.length > 0;

	const pagedSearch = useMemo(() => {
		if (!isSearchMode) return [];
		const start = searchPage * PAGE_SIZE;
		return searchResults.slice(start, start + PAGE_SIZE);
	}, [isSearchMode, searchResults, searchPage]);

	const goToPage = async (oneBasedPage) => {
		const target = Math.max(
			0,
			Math.min(oneBasedPage - 1, Math.max(0, totalPages - 1)),
		);
		if (isSearchMode) {
			setSearchPage(target);
		} else {
			await loadPage(target);
			await ensureCursorsUpTo(target);
			computeAllPages();
		}
	};

	const itemsToRender = isSearchMode ? pagedSearch : patterns;
	const currentPage = (isSearchMode ? searchPage : page) + 1;

	return {
		patterns: itemsToRender,
		loading,
		searchError,
		searchTerm,
		setSearchTerm,
		totalPages,
		totalCount,
		currentPage,
		goToPage,
	};
};
